import type { MerkmalZahl } from '../planungKiAnzeige';
import type { MerkmalSchalter } from '../planungVorbelegung';
import type { ProjektMerkmale } from '../types';

/**
 * Die Fragen des gefuehrten Dialogs (Betreiber 01.11.2026: „Mitarbeiter
 * oeffnet die Planungsphase und wird wie durch Zauberhand durch den Dialog
 * gefuehrt").
 *
 * Statt einer Maske mit 22 Drei-Weg-Schaltern stellt der Dialog EINE Frage
 * je Bildschirm, in Alltagssprache und in der Reihenfolge, in der man ein
 * Vorhaben erzaehlt. Gefragt wird nur, was weder die Projektdaten noch die
 * KI beantwortet haben (`offeneDialogFragen`). „Weiss nicht" setzt nichts:
 * das Merkmal bleibt ungeklaert, die Fragen dazu bleiben im Katalog und
 * gehen an die Beteiligten — dieselbe dreiwertige Hausregel wie in der Maske.
 *
 * Die Paragraphen stehen nur im aufklappbaren „Warum?", nie in der Frage.
 */

export type DialogFeld =
    | { art: 'ja_nein'; feld: MerkmalSchalter }
    | { art: 'zahl'; feld: MerkmalZahl; einheit: string; ganzzahl: boolean; min: number; max: number }
    | { art: 'lph' }
    /** Mehrere seltene Merkmale auf einem Bildschirm: „Nein, nichts davon"
     *  setzt alle noch offenen auf Nein, „Ja" laesst die zutreffenden waehlen. */
    | { art: 'gruppe'; felder: readonly MerkmalSchalter[] };

export interface DialogFrage {
    /** Stabil: Merkmalsschluessel, `lph` oder `gruppe:anhang2`. */
    id: string;
    feld: DialogFeld;
    frage: string;
    /** Sichtbar unter der Frage — nur, wo ein falsches Nein naheliegt. */
    erlaeuterung?: string;
    /** Ein Satz hinter „Warum?". */
    warum: string;
    /** Nur stellen, wenn die Bedingung auf den Entwurf zutrifft (Baujahr nur
     *  bei Bestand usw.). Fehlt sie, gilt die Frage immer. */
    wenn?: (m: ProjektMerkmale) => boolean;
}

const jaNein = (feld: MerkmalSchalter, frage: string, warum: string): DialogFrage =>
    ({ id: feld, feld: { art: 'ja_nein', feld }, frage, warum });

const zahl = (
    feld: MerkmalZahl,
    frage: string,
    warum: string,
    einheit: string,
    grenzen: { ganzzahl: boolean; min: number; max: number },
    wenn?: (m: ProjektMerkmale) => boolean,
): DialogFrage => ({ id: feld, feld: { art: 'zahl', feld, einheit, ...grenzen }, frage, warum, ...(wenn ? { wenn } : {}) });

/** Die besonders gefaehrlichen Arbeiten nach Anhang II — fuer die meisten
 *  Vorhaben ein einziges „Nein, nichts davon". */
export const ANHANG2_FELDER: readonly MerkmalSchalter[] = [
    'freileitung', 'gewaesser', 'tunnelbau', 'taucharbeiten', 'druckluft', 'sprengarbeiten', 'strahlung', 'gefahrstoffeAnhang2',
];

/** Kurze Namen der Anhang-II-Arbeiten fuer die Auswahl. */
export const ANHANG2_LABEL: Record<string, string> = {
    freileitung: 'Hochspannungsfreileitung in der Nähe',
    gewaesser: 'Arbeiten an oder über Wasser',
    tunnelbau: 'Brunnen-, Tunnelbau oder unterirdische Erdarbeiten',
    taucharbeiten: 'Taucherarbeiten',
    druckluft: 'Arbeiten in Druckluft',
    sprengarbeiten: 'Sprengarbeiten',
    strahlung: 'Ionisierende Strahlung (Kontrollbereich)',
    gefahrstoffeAnhang2: 'Krebserzeugende oder explosive Stoffe, Biostoffe der Risikogruppe 3/4',
};

/** Leistungsphasen nach HOAI fuer die Auswahl. */
export const LPH_LABEL: Record<number, string> = {
    1: 'Grundlagenermittlung',
    2: 'Vorplanung',
    3: 'Entwurfsplanung',
    4: 'Genehmigungsplanung',
    5: 'Ausführungsplanung',
    6: 'Vorbereitung der Vergabe',
    7: 'Mitwirkung bei der Vergabe',
    8: 'Objektüberwachung',
    9: 'Objektbetreuung',
};

const GANZ = (min: number, max: number) => ({ ganzzahl: true, min, max });
const KOMMA = (min: number, max: number) => ({ ganzzahl: false, min, max });

/**
 * Alle Fragen in Dialogreihenfolge: erst Umfang und Pflichten (sie
 * entscheiden ueber Vorankuendigung und SiGe-Plan), dann das Vorhaben wie
 * es gebaut wird, zuletzt die seltenen gefaehrlichen Arbeiten als Gruppe.
 * Jedes Ja/Nein-Merkmal der Maske (`MERKMAL_SCHLUESSEL`) und jedes Feld der
 * KI (`KI_FELDER`) kommt genau einmal vor — der Test haelt das fest.
 */
export const DIALOG_FRAGEN: readonly DialogFrage[] = [
    // Nicht „gleichzeitig": die Pflicht nach § 3 Abs. 1 gilt auch fuer
    // Firmen, die nacheinander arbeiten (Codex 01.11.) — ein richtiges Nein
    // auf „gleichzeitig" haette sonst Fragen ausgeblendet.
    {
        ...jaNein('mehrereArbeitgeber',
            'Arbeiten Beschäftigte mehrerer Firmen auf der Baustelle, ob gleichzeitig oder nacheinander?',
            'Dann braucht es einen Koordinator, und bei gefährlichen Arbeiten oder Vorankündigung einen SiGe-Plan (§ 2 Abs. 3, § 3 Abs. 1 BaustellV).'),
        // Wie die Maske und PL-A-10 — wer an den Generalunternehmer allein
        // denkt, antwortete sonst Nein (Opus-Review 01.11.).
        erlaeuterung: 'Mitgezählt werden auch Nachunternehmer, Lieferanten mit Montage und Eigenleistungen des Bauherrn.',
    },
    zahl('arbeitstage',
        'Wie viele Arbeitstage dauert der Bau voraussichtlich?',
        'Mehr als 30 Arbeitstage mit mehr als 20 Beschäftigten gleichzeitig machen eine Vorankündigung nötig (§ 2 Abs. 2 BaustellV).',
        'Arbeitstage', GANZ(1, 3000)),
    zahl('maxBeschaeftigte',
        'Wie viele Personen arbeiten höchstens gleichzeitig auf der Baustelle?',
        'Mehr als 20 gleichzeitig bei mehr als 30 Arbeitstagen machen eine Vorankündigung nötig (§ 2 Abs. 2 BaustellV).',
        'Personen', GANZ(1, 10000)),
    zahl('personentage',
        'Wie viele Personentage umfassen die Arbeiten insgesamt?',
        'Mehr als 500 Personentage machen eine Vorankündigung nötig (§ 2 Abs. 2 BaustellV).',
        'Personentage', GANZ(1, 1000000)),
    { id: 'lph', feld: { art: 'lph' },
        frage: 'In welcher Leistungsphase steht die Planung?',
        warum: 'Fragen, die erst in einer späteren Phase beeinflussbar sind, kommen dann von selbst dazu.' },

    jaNein('bestand',
        'Wird im Bestand gebaut (Umbau, Sanierung oder Anbau)?',
        'Bauen im Bestand bringt Fragen zu Schadstoffen, Standsicherheit und laufender Nutzung.'),
    zahl('bestandBaujahr',
        'Aus welchem Jahr stammt der Bestand?',
        'Vor dem 31.10.1993 gilt die Asbestvermutung.',
        '', GANZ(1000, 2100), m => m.bestand === true),
    jaNein('betriebLaeuftWeiter',
        'Wird das Gebäude während der Bauzeit weiter genutzt?',
        'Nutzer und Baustelle müssen dann sicher voneinander getrennt werden.'),
    jaNein('abbruch',
        'Wird etwas abgebrochen oder zurückgebaut?',
        'Abbruch bringt Fragen zu Standsicherheit, Schadstoffen und Entsorgung.'),
    jaNein('erdarbeiten',
        'Gibt es Erdarbeiten, eine Baugrube oder Gräben?',
        'Erdarbeiten bringen Fragen zu Baugrund, Leitungen und Verbau.'),
    zahl('grabentiefeM',
        'Wie tief wird die tiefste Baugrube oder der tiefste Graben?',
        'Mehr als 5 m zählen zu den besonders gefährlichen Arbeiten (Anhang II Nr. 1 BaustellV).',
        'm', KOMMA(0.1, 200), m => m.erdarbeiten === true),
    jaNein('altlastenverdacht',
        'Gibt es einen Verdacht auf Altlasten oder belasteten Boden?',
        'Dann werden Gutachten und Schutzmaßnahmen abgefragt.'),
    jaNein('strassenbaustelle',
        'Wird im öffentlichen Verkehrsraum gearbeitet?',
        'Dann geht es um Verkehrssicherung und verkehrsrechtliche Anordnung.'),

    jaNein('kran',
        'Wird ein Turmdreh- oder Autokran eingesetzt?',
        'Kranstandort, Überschwenkbereiche und Nachbarn müssen geklärt werden.'),
    jaNein('fertigteilmontage',
        'Werden Fertigteile oder schwere Bauelemente montiert?',
        'Montage mit Hebezeug zählt zu den besonders gefährlichen Arbeiten (Anhang II Nr. 10 BaustellV).'),
    jaNein('ortbeton',
        'Wird vor Ort geschalt, bewehrt und betoniert?',
        'Schal- und Bewehrungsarbeiten bringen eigene Fragen zu Absturz und Ablauf.'),
    jaNein('geruest',
        'Wird ein Gerüst gebraucht?',
        'Gerüste nutzen meist mehrere Gewerke, deshalb sind Bereitstellung und Prüfung zu klären.'),
    jaNein('dacharbeiten',
        'Wird am Dach gearbeitet?',
        'Dacharbeiten bringen Fragen zur Absturzsicherung, auch für spätere Wartung.'),
    zahl('absturzhoeheM',
        'Wie hoch ist die größte Absturzhöhe?',
        'Mehr als 7 m zählen zu den besonders gefährlichen Arbeiten (Anhang II Nr. 1 BaustellV).',
        'm', KOMMA(0.1, 1000), m => m.geruest === true || m.dacharbeiten === true),
    jaNein('tga',
        'Gibt es Haustechnik in nennenswertem Umfang (Heizung, Lüftung, Elektro)?',
        'Viele Gewerke der Haustechnik brauchen eine abgestimmte Reihenfolge.'),
    jaNein('nachtarbeit',
        'Wird nachts, im Schichtbetrieb oder am Wochenende gearbeitet?',
        'Dann sind Beleuchtung, Aufsicht und Erste Hilfe außerhalb der üblichen Zeiten zu klären.'),
    jaNein('winterbau',
        'Fällt die Bauzeit in den Winter?',
        'Winterbau bringt Fragen zu Witterungsschutz, Beleuchtung und Glätte.'),
    jaNein('unternehmerOhneBeschaeftigte',
        'Arbeiten auch Selbstständige ohne eigene Beschäftigte mit?',
        'Für sie gelten eigene Pflichten auf der Baustelle (§ 6 BaustellV).'),

    { id: 'gruppe:anhang2', feld: { art: 'gruppe', felder: ANHANG2_FELDER },
        frage: 'Kommt eine dieser besonders gefährlichen Arbeiten vor?',
        warum: 'Arbeiten nach Anhang II BaustellV machen bei mehreren Firmen einen SiGe-Plan zur Pflicht (§ 2 Abs. 3 BaustellV).' },
];
